// hooks/useTraining.ts
//
// The signed-in crew member's own training. Everything in useHr.ts talks to
// the HR-only endpoints, which 403 for a crew login — these all hit
// /api/training/my/, which is scoped to request.user.team_member_profile.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type {
  MyTrainingRow,
  MyTrainingSummary,
  MyTrainingDetail,
  MyTrainingQuiz,
  MyTrainingCompletion,
  MyTrainingQuizResult,
  MyTrainingFilters,
} from '../types/training';

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** The shape ApiClient hangs on the Error it throws for a non-2xx response. */
interface ApiError {
  status?: number;
  responseData?: { detail?: string; error?: string };
}

const asApiError = (error: unknown): ApiError =>
  (error && typeof error === 'object' ? (error as ApiError) : {});

/**
 * True when the caller's login has no TeamMember behind it. The learner API
 * answers 403 with this, and it is the one failure a crew member can act on
 * ("ask HR to link it") rather than a permission problem.
 */
export const isUnlinkedLoginError = (error: unknown): boolean => {
  const { status, responseData } = asApiError(error);
  return status === 403 && !!responseData?.detail?.includes('not linked to a team member');
};

/**
 * The server's own words for a failure. DRF answers these routes with either
 * `detail` (permissions) or `error` (retakes exhausted, wrong content type),
 * and both are written for the person reading them.
 */
export const trainingErrorMessage = (error: unknown, fallback: string): string => {
  const { responseData } = asApiError(error);
  if (typeof responseData?.error === 'string') return responseData.error;
  if (typeof responseData?.detail === 'string') return responseData.detail;
  return responseData ? Object.entries(responseData).map(([key, value]) => `${key.replaceAll('_', ' ')}: ${Array.isArray(value) ? value.join(', ') : String(value)}`).join(' · ') : fallback;
};

const buildParams = (filters?: MyTrainingFilters): string => {
  const params = new URLSearchParams();
  for (const key of ["page", "view", "due", "content_type"] as const) if (filters?.[key]) params.set(key, String(filters[key]));
  if (filters?.status) params.append('status', filters.status);
  if (filters?.is_mandatory !== undefined) params.append('is_mandatory', String(filters.is_mandatory));
  if (filters?.search) params.append('search', filters.search);
  if (filters?.ordering) params.append('ordering', filters.ordering);
  if (filters?.page_size) params.append('page_size', String(filters.page_size));
  return params.toString();
};

export const useMyTraining = (filters?: MyTrainingFilters) => {
  return useQuery({
    queryKey: ['my-training', filters],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<MyTrainingRow>>(
        `/api/training/my/?${buildParams(filters)}`
      );
      return response.data;
    },
  });
};

export const useMyTrainingSummary = () => {
  return useQuery({
    queryKey: ['my-training-summary'],
    queryFn: async () => {
      const response = await apiClient.get<MyTrainingSummary>('/api/training/my/summary/');
      return response.data;
    },
  });
};

/**
 * One item, with everything the player needs to render it.
 *
 * Fetching this marks the item started server-side, so it is deliberately
 * never invalidated by the mutations below — they patch this cache instead.
 */
export const useMyTrainingDetail = (progressId?: string) => {
  return useQuery({
    queryKey: ['my-training-detail', progressId],
    queryFn: async () => {
      const response = await apiClient.get<MyTrainingDetail>(`/api/training/my/${progressId}/`);
      return response.data;
    },
    enabled: !!progressId,
  });
};

export const useMyTrainingQuiz = (progressId?: string, enabled = true) => {
  return useQuery({
    queryKey: ['my-training-quiz', progressId],
    queryFn: async () => {
      const response = await apiClient.get<MyTrainingQuiz>(`/api/training/my/${progressId}/quiz/`);
      return response.data;
    },
    enabled: !!progressId && enabled,
  });
};

/**
 * Shared cache housekeeping after a write: the row the player is showing is
 * patched in place, the list and the summary tiles are refetched.
 */
const useApplyProgress = () => {
  const queryClient = useQueryClient();

  return (progressId: string, progress: MyTrainingRow) => {
    queryClient.setQueryData<MyTrainingDetail>(
      ['my-training-detail', progressId],
      (current) => (current ? { ...current, progress } : current)
    );
    queryClient.invalidateQueries({ queryKey: ['my-training'] });
    queryClient.invalidateQueries({ queryKey: ['my-training-summary'] });
    queryClient.invalidateQueries({ queryKey: ['my-training-paths'] });
    queryClient.invalidateQueries({ queryKey: ['my-training-transcript'] });
  };
};

/** Record playback position / watched percentage. Moves not_started → in_progress. */
export const useRecordMyTrainingProgress = () => {
  const applyProgress = useApplyProgress();

  return useMutation({
    scope: { id: 'learner-progress-writes' },
    mutationFn: async ({
      progressId,
      watchPercentage,
      progressData,
    }: {
      progressId: string;
      watchPercentage?: number;
      progressData?: Record<string, unknown>;
    }) => {
      const response = await apiClient.post<MyTrainingRow>(
        `/api/training/my/${progressId}/progress/`,
        { watch_percentage: watchPercentage, progress_data: progressData }
      );
      return response.data;
    },
    onSuccess: (progress, { progressId }) => applyProgress(progressId, progress),
  });
};

export const useCompleteMyTraining = () => {
  const applyProgress = useApplyProgress();

  return useMutation({
    mutationFn: async ({
      progressId,
      checklistCompleted,
      confirmation,
    }: {
      progressId: string;
      checklistCompleted?: string[];
      confirmation?: boolean;
    }) => {
      const response = await apiClient.post<MyTrainingCompletion>(
        `/api/training/my/${progressId}/complete/`,
        { checklist_completed: checklistCompleted, confirmation }
      );
      return response.data;
    },
    onSuccess: (result, { progressId }) => applyProgress(progressId, result.progress),
  });
};

/**
 * Submit a quiz attempt. Retakes and the attempt ceiling are enforced by the
 * server (HrSettings is HR-only, so the learner only finds out by being told).
 */
export const useSubmitMyTrainingQuiz = () => {
  const queryClient = useQueryClient();
  const applyProgress = useApplyProgress();

  return useMutation({
    mutationFn: async ({
      progressId,
      answers,
    }: {
      progressId: string;
      answers: Record<string, string>;
    }) => {
      const response = await apiClient.post<MyTrainingQuizResult>(
        `/api/training/my/${progressId}/quiz/`,
        { answers }
      );
      return response.data;
    },
    onSuccess: (result, { progressId }) => {
      applyProgress(progressId, result.progress);
      // The attempt counter lives on the quiz payload too.
      queryClient.invalidateQueries({ queryKey: ['my-training-quiz', progressId] });
    },
  });
};
